import * as React from 'react';
export const IndexContext = /*#__PURE__*/ React.createContext(null);
IndexContext.displayName = 'IndexContext';
function parseIndexPath(indexPathString) {
    return indexPathString.split('.').map((index)=>parseInt(index, 10));
}
// Returns the index path data based on the closest `useIndexedChildren`
export function useIndex() {
    const indexPathString = React.useContext(IndexContext);
    return React.useMemo(()=>{
        if (indexPathString === null) {
            return null;
        }
        const indexPath = parseIndexPath(indexPathString);
        const index = indexPath[indexPath.length - 1];
        return {
            index,
            indexPath,
            indexPathString
        };
    }, [
        indexPathString
    ]);
}
// Provides the current index path for each child
export function useIndexedChildren(children, nested = false) {
    const indexPathString = React.useContext(IndexContext);
    const sep = nested ? '>' : '.';
    return React.Children.map(children, (child, index)=>/*#__PURE__*/ React.isValidElement(child) ? /*#__PURE__*/ React.createElement(IndexContext.Provider, {
            key: child.key,
            value: indexPathString ? `${indexPathString}${sep}${index.toString()}` : index.toString()
        }, child) : child);
}
export function IndexedChildren({ children }) {
    return /*#__PURE__*/ React.createElement(React.Fragment, null, useIndexedChildren(children));
}
export function useIndexedGroup(children) {
    return useIndexedChildren(children, true);
}
export function IndexedGroup({ children }) {
    return /*#__PURE__*/ React.createElement(React.Fragment, null, useIndexedGroup(children));
}
const indexSeparatorRegex = /[.>]/;
export function compareIndexPaths(a = '', b = '') {
    const aArray = a.split(indexSeparatorRegex).map(Number);
    const bArray = b.split(indexSeparatorRegex).map(Number);
    if (aArray.includes(NaN) || bArray.includes(NaN)) {
        throw new Error('Version contains parts that are not numbers');
    }
    const maxLength = Math.max(a.length, b.length);
    for(let index = 0; index < maxLength; index++){
        if (index >= aArray.length && index < bArray.length) {
            return -1;
        } else if (index >= bArray.length && index < aArray.length) {
            return 1;
        }
        var _aArray_index, _bArray_index;
        const difference = ((_aArray_index = aArray[index]) !== null && _aArray_index !== void 0 ? _aArray_index : 0) - ((_bArray_index = bArray[index]) !== null && _bArray_index !== void 0 ? _bArray_index : 0);
        if (difference === 0) {
            continue;
        }
        return difference > 0 ? 1 : -1;
    }
    return 0;
}
function getChildPathIndex(item, parent) {
    if (item.indexPathString.startsWith(parent.indexPathString)) {
        const idx = item.indexPathString.indexOf('>', parent.indexPathString.length);
        return idx === -1 ? idx : idx + 1;
    }
    return -1;
}
function isChild(item, parent) {
    return getChildPathIndex(item, parent) !== -1;
}
function getRelativePath(item, parent) {
    let path = item.indexPathString;
    if (parent) {
        const idx = getChildPathIndex(item, parent);
        if (idx === -1) {
            return '';
        }
        path = item.indexPathString.slice(idx);
    }
    if (path.includes('>')) {
        // This is a very low-level error and there isn't a great way to explain
        // this. External users shouldn't see this as components will set this up
        // correctly. This happens if you use `useIndexedGroup` in a place where
        // there is no `useItem` registered above it. Nested items must always have
        // a parent; we don't support nested arrays, it must always be an item with
        // a `childNodes` prop
        throw new Error('A collection group was used outside of a collection item. ' + 'Groups must have a parent. Register the parent item or move the group inside ' + 'an item.');
    }
    return item.indexPathString;
}
function _treeFromItems(items, // eslint-disable-next-line default-param-last
initialPointer = 0, parent) {
    const results = [];
    let pointer = initialPointer;
    while(pointer < items.length){
        const item = {
            ...items[pointer]
        };
        const path = getRelativePath(item, parent);
        if (path === '') {
            return {
                results,
                pointer
            };
        }
        results.push(item);
        pointer++;
        if (pointer < items.length && isChild(items[pointer], item)) {
            const recursed = _treeFromItems(items, pointer, item);
            item.childItems = recursed.results;
            pointer = recursed.pointer;
        }
    }
    return {
        results,
        pointer
    };
}
export function treeFromItems(items) {
    return _treeFromItems(items).results;
}

//# sourceMappingURL=useIndexedChildren.js.map